import { ThemeEvents } from '@theme/events';

const STORE_AVAILABILITY_QUERY = `
  query StoreAvailability($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        id
        availableForSale
        storeAvailability(first: 20) {
          nodes {
            quantityAvailable
            location {
              id
              name
              address {
                address1
                city
                zip
                phone
              }
            }
          }
        }
      }
    }
  }
`;

const CHECKMARK_ICON = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark" aria-hidden="true">
<path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const CLOSE_ICON = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/**
 * @typedef {Object} StoreAvailabilityConfig
 * @property {string} storefrontToken
 * @property {string} shopDomain
 * @property {string} apiVersion
 * @property {{ availableIn: string, store: string, stores: string, closeDialog: string }} labels
 * @property {{ key: string, url: string }[]} locationPageMapping
 */

/**
 * @typedef {Object} StoreAvailabilityLocation
 * @property {number} quantityAvailable
 * @property {{ id?: string, name: string, address: { address1?: string, city?: string, zip?: string, phone?: string } }} location
 */

/**
 * @typedef {Object} LocationPageMapping
 * @property {Map<string, string>} byId
 * @property {Map<string, string>} byName
 */

/**
 * @param {string} url
 * @returns {string | null}
 */
function normalizeLocationUrl(url) {
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `/pages/${trimmed.replace(/^\/pages\//, '')}`;
}

/**
 * @param {{ key: string, url: string }[] | undefined} entries
 * @returns {LocationPageMapping}
 */
function buildLocationPageMapping(entries) {
  /** @type {Map<string, string>} */
  const byId = new Map();
  /** @type {Map<string, string>} */
  const byName = new Map();

  for (const entry of entries || []) {
    const key = String(entry?.key || '').trim();
    const url = normalizeLocationUrl(String(entry?.url || ''));
    if (!key || !url) continue;

    if (/^\d+$/.test(key)) {
      byId.set(key, url);
    }
    byName.set(key.toLowerCase(), url);
  }

  return { byId, byName };
}

/**
 * @param {{ id?: string, name?: string }} location
 * @param {LocationPageMapping} mapping
 * @returns {string | null}
 */
function resolveLocationPageUrl(location, mapping) {
  const gid = location.id;
  const numericId = typeof gid === 'string' ? gid.match(/\/(\d+)$/)?.[1] : null;

  if (numericId && mapping.byId.has(numericId)) {
    return mapping.byId.get(numericId) || null;
  }

  const nameKey = location.name?.trim().toLowerCase();
  if (nameKey && mapping.byName.has(nameKey)) {
    return mapping.byName.get(nameKey) || null;
  }

  return null;
}

class StoreAvailability extends HTMLElement {
  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {StoreAvailabilityConfig | null} */
  #config = null;

  /** @type {LocationPageMapping} */
  #locationPageMapping = buildLocationPageMapping([]);

  connectedCallback() {
    this.#config = this.#readConfig();
    if (!this.#config?.storefrontToken) {
      this.setAttribute('hidden', '');
      return;
    }

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.#fetchAvailability(this.dataset.variantId);
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#activeFetch?.abort();
  }

  /**
   * @returns {StoreAvailabilityConfig | null}
   */
  #readConfig() {
    const configElement = this.querySelector('[data-store-availability-config]');
    if (!(configElement instanceof HTMLScriptElement) || !configElement.textContent) return null;

    try {
      const config = JSON.parse(configElement.textContent);
      this.#locationPageMapping = buildLocationPageMapping(config.locationPageMapping);
      return config;
    } catch {
      return null;
    }
  }

  /** @type {(event: import('@theme/events').VariantUpdateEvent) => void} */
  #onVariantUpdate = (event) => {
    const productInventory = this.closest('product-inventory');
    const productId = event.detail.data.productId;

    if (
      productInventory instanceof HTMLElement &&
      productInventory.dataset.productId &&
      productId &&
      productInventory.dataset.productId !== productId &&
      !event.detail.data.newProduct
    ) {
      return;
    }

    const variantId = event.detail.resource?.id;
    if (!variantId) {
      this.setAttribute('hidden', '');
      return;
    }

    this.dataset.variantId = String(variantId);
    queueMicrotask(() => this.#fetchAvailability(String(variantId)));
  };

  /**
   * @param {string | undefined} variantId
   */
  #fetchAvailability(variantId) {
    if (!variantId || !this.#config?.storefrontToken) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    const url = `https://${this.#config.shopDomain}/api/${this.#config.apiVersion}/graphql.json`;

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.#config.storefrontToken,
      },
      body: JSON.stringify({
        query: STORE_AVAILABILITY_QUERY,
        variables: { id: `gid://shopify/ProductVariant/${variantId}` },
      }),
      signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Storefront API error (${response.status})`);
        return response.json();
      })
      .then((payload) => {
        if (signal.aborted) return;

        if (payload.errors?.length) {
          throw new Error('Storefront GraphQL errors');
        }

        /** @type {StoreAvailabilityLocation[]} */
        const locations = (payload.data?.node?.storeAvailability?.nodes || []).filter(
          (node) => node.quantityAvailable > 0
        );

        if (!payload.data?.node?.availableForSale || locations.length === 0) {
          this.setAttribute('hidden', '');
          this.#clearContent();
          return;
        }

        this.#render(locations);
        this.removeAttribute('hidden');
      })
      .catch((error) => {
        if (signal.aborted || error.name === 'AbortError') return;
        this.setAttribute('hidden', '');
        this.#clearContent();
      });
  }

  #clearContent() {
    const content = this.querySelector('[data-store-availability-content]');
    if (content) content.innerHTML = '';
  }

  /**
   * @param {StoreAvailabilityLocation[]} locations
   */
  #render(locations) {
    const content = this.querySelector('[data-store-availability-content]');
    const config = this.#config;
    const popupId = this.dataset.popupId;

    if (!(content instanceof HTMLElement) || !config || !popupId) return;

    const count = locations.length;
    const storeLabel = count === 1 ? config.labels.store : config.labels.stores;
    const listItems = locations
      .map((entry) => {
        const { location } = entry;
        const address = location.address || {};
        const pageUrl = resolveLocationPageUrl(location, this.#locationPageMapping);
        const nameHtml = pageUrl
          ? `<a href="${escapeHtml(pageUrl)}" class="tw:font-semibold tw:underline tw:text-black hover:tw:opacity-80">${escapeHtml(location.name)}</a>`
          : `<strong class="tw:font-semibold">${escapeHtml(location.name)}</strong>`;

        return `<li class="tw:*:block tw:mb-2.5">
    ${nameHtml}
    <span>${escapeHtml(address.address1 || '')}</span>
    <span>${escapeHtml([address.zip, address.city].filter(Boolean).join(' '))}</span>
    <span>${escapeHtml(address.phone || '')}</span>
  </li>`;
      })
      .join('\n');

    content.innerHTML = `
<div class="stock-message tw:flex tw:items-center tw:gap-2 tw:text-black stock-message--in_stock">
  <span class="stock-message__icon tw:flex tw:items-center tw:justify-center tw:w-4 tw:h-4 tw:rounded-full">
    <span class="svg-wrapper tw:w-3 tw:h-3">${CHECKMARK_ICON}</span>
  </span>
  <span class="stock-message__text">
    <span class="store-availability__text">
      ${escapeHtml(config.labels.availableIn)}
      <button class="button-unstyled tw:inline-block tw:underline tw:cursor-pointer tw:text-2xs" popovertarget="${escapeHtml(popupId)}" type="button">
        ${count} ${escapeHtml(storeLabel)}
      </button>
    </span>
  </span>
</div>
<dialog
  id="${escapeHtml(popupId)}"
  popover="auto"
  class="tw:relative tw:m-2.5 tw:w-fit tw:max-w-md tw:border tw:border-medium-grey tw:bg-light-grey tw:p-4 tw:pr-18 tw:rounded-xl tw:text-black"
  style="position-area: top;"
>
  <button
    type="button"
    class="button button-unstyled close-button tw:absolute tw:right-4 tw:top-4 tw:flex tw:h-9 tw:w-9 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand-yellow tw:text-black"
    popovertarget="${escapeHtml(popupId)}"
    popovertargetaction="hide"
    aria-label="${escapeHtml(config.labels.closeDialog)}"
  >
    <span class="svg-wrapper" aria-hidden="true">${CLOSE_ICON}</span>
  </button>
  <div>
    <ul>${listItems}</ul>
  </div>
</dialog>`;
  }
}

if (!customElements.get('store-availability')) {
  customElements.define('store-availability', StoreAvailability);
}
